export type PromoOfferActivationType = 'integration' | 'discount'
export type PromoRelevance = 'available' | 'already_activated' | 'already_installed'

export function readPromoRelevance(raw: unknown): PromoRelevance | undefined {
    const value = String(raw || '').trim().toLowerCase()
    if (value === 'available' || value === 'already_activated' || value === 'already_installed') {
        return value
    }
    return undefined
}

/**
 * Type sent to promo/activate when the caller did not pick one.
 * One option is used as-is. Several options prefer integration, otherwise the first.
 */
export function resolveDefaultActivationType(
    types: PromoOfferActivationType[] | null | undefined
): PromoOfferActivationType {
    const list = (types || []).filter(
        (type) => type === 'integration' || type === 'discount'
    )
    if (list.length === 1) return list[0]
    if (list.includes('integration')) return 'integration'
    if (list.length > 0) return list[0]
    return 'integration'
}

export function readAvailableActivationTypes(
    raw: unknown
): PromoOfferActivationType[] | undefined {
    if (!Array.isArray(raw)) return undefined
    const types: PromoOfferActivationType[] = []
    for (const item of raw) {
        const value = String(item || '').trim().toLowerCase()
        if ((value === 'integration' || value === 'discount') && !types.includes(value)) {
            types.push(value)
        }
    }
    return types
}

/**
 * `relevance` decides whether to offer the promo.
 * `availableActivationTypes` is only what the campaign can do, and stays the same at any status.
 * A missing `relevance` keeps the previous lifecycle rules.
 */
export function promoCanBeOffered(promo: {
    status?: string | null
    relevance?: PromoRelevance | null
    availableActivationTypes?: PromoOfferActivationType[] | null
}): boolean {
    if (promo.relevance === 'already_installed') return false
    if (promo.relevance === 'already_activated') return promo.status === 'activating'
    if (promo.relevance === 'available') {
        if (promo.status === 'activating') return true
        if (promo.availableActivationTypes == null) return true
        return promo.availableActivationTypes.length > 0
    }
    if (promo.status === 'activating') return true
    if (promo.status !== 'available') return false
    if (promo.availableActivationTypes == null) return true
    return promo.availableActivationTypes.length > 0
}

/**
 * Both types at once: SMS already on the project → discount, otherwise the free channel.
 * A single type is shown as-is. An empty list offers nothing.
 * A missing list keeps the previous SMS-exists rule.
 */
export function resolveSmsOfferKind(
    types: PromoOfferActivationType[] | null | undefined,
    smsExists: boolean
): 'integration' | 'discount' | null {
    if (types == null) return smsExists ? 'discount' : 'integration'
    const integration = types.includes('integration')
    const discount = types.includes('discount')
    if (integration && discount) return smsExists ? 'discount' : 'integration'
    if (integration) return 'integration'
    if (discount) return 'discount'
    return null
}

/** Trial activation is allowed only when the current offer is the free channel, not the discount. */
export function smsPromoAllowsIntegration(
    promo: {
        status?: string | null
        relevance?: PromoRelevance | null
        availableActivationTypes?: PromoOfferActivationType[] | null
    } | null | undefined,
    smsExists: boolean
): boolean {
    if (!promo || promo.status === 'activating') return false
    if (promo.relevance && promo.relevance !== 'available') return false
    return resolveSmsOfferKind(promo.availableActivationTypes, smsExists) === 'integration'
}
